import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { lookupUkPostcode, type UkAddressOption, type UkPostcodeResult } from '@/services/ukPostcode';

export type UkPostcodeFill = {
  postal_code: string;
  city?: string;
  province?: string;
  address?: string;
};

type Props = {
  postalCode: string;
  onPostalCodeChange: (value: string) => void;
  onFill: (fields: UkPostcodeFill) => void;
  inputClassName?: string;
  disabled?: boolean;
};

/**
 * Postcode input + Find address. When Ideal returns streets, shows a select list.
 * Falls back to city/region-only fill via postcodes.io.
 */
export default function UkPostcodeLookup({
  postalCode,
  onPostalCodeChange,
  onFill,
  inputClassName = 'input-field flex-1',
  disabled,
}: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hint, setHint] = useState('');
  const [addresses, setAddresses] = useState<UkAddressOption[]>([]);
  const [resultMeta, setResultMeta] = useState<UkPostcodeResult | null>(null);

  const runLookup = async () => {
    setLoading(true);
    setError('');
    setHint('');
    setAddresses([]);
    setResultMeta(null);
    try {
      const r = await lookupUkPostcode(postalCode);
      setResultMeta(r);
      onPostalCodeChange(r.postcode);

      if (r.source === 'ideal' && r.addresses.length > 0) {
        setAddresses(r.addresses);
        setHint(t('ukAddress.selectAddressHint'));
        // Prefill city/province; wait for street pick for full address
        onFill({
          postal_code: r.postcode,
          city: r.city || r.addresses[0]?.city,
          province: r.province,
        });
      } else {
        onFill({
          postal_code: r.postcode,
          city: r.city,
          province: r.province,
        });
        setHint(
          r.fallback
            ? t('ukAddress.fallbackFree')
            : t('ukAddress.freeLookupOnly'),
        );
      }
    } catch (err) {
      setError((err as Error)?.message || t('ukAddress.lookupFailed'));
    } finally {
      setLoading(false);
    }
  };

  const pickAddress = (idx: number) => {
    const a = addresses[idx];
    if (!a) return;
    onFill({
      postal_code: a.postcode || resultMeta?.postcode || postalCode,
      city: a.city || a.post_town,
      province: resultMeta?.province,
      address: a.address || a.formatted,
    });
    setHint(t('ukAddress.addressApplied'));
  };

  return (
    <div className="space-y-2 min-w-0">
      <div className="flex gap-2 min-w-0">
        <input
          className={inputClassName}
          value={postalCode}
          disabled={disabled}
          onChange={(e) => {
            setError('');
            setHint('');
            setAddresses([]);
            onPostalCodeChange(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void runLookup();
            }
          }}
        />
        <button
          type="button"
          className="btn-secondary text-xs px-3 whitespace-nowrap inline-flex items-center gap-1.5"
          disabled={disabled || loading}
          onClick={() => void runLookup()}
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          {t('ukAddress.findAddress')}
        </button>
      </div>
      {addresses.length > 0 && (
        <select
          className="input-field w-full text-sm"
          defaultValue=""
          onChange={(e) => {
            const idx = Number(e.target.value);
            if (!Number.isNaN(idx)) pickAddress(idx);
          }}
        >
          <option value="" disabled>
            {t('ukAddress.selectAddress')} ({addresses.length})
          </option>
          {addresses.map((a, i) => (
            <option key={`${a.formatted}-${i}`} value={i}>
              {a.formatted || a.address}
            </option>
          ))}
        </select>
      )}
      {error && <p className="text-xs text-red-500">{error}</p>}
      {!error && hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  );
}
